import { logger } from './logger';
import { StorageData, PromptData, FolderData, UserSettingsData } from '../types/storage';
import type { PlatformStorageChange } from '../infrastructure/platform';

/**
 * Serializes data for storage with metadata
 * @param data The data to serialize
 * @returns StorageData object with the data and timestamp
 */
export function serializeForStorage<T>(data: T): StorageData<T> {
  //console.log('storageUtils.ts serializeForStorage: Input data:', data);
  const result = {
    data,
    lastSync: new Date().toISOString(),
  };
  //console.log('storageUtils.ts serializeForStorage: Output data:', result);
  return result;
}

/**
 * Deserializes data from storage
 * @param storageData The storage data to deserialize
 * @returns The deserialized data or null if not found
 */
export function deserializeFromStorage<T>(
  storageData: StorageData<T> | null | undefined
): T | null {
  if (!storageData) return null;
  // If storageData is already the data (not wrapped), return it directly
  if (!('data' in storageData)) return storageData as T;
  return storageData.data;
}

/**
 * Validates that the data is an array of prompt objects
 * @param data The data to validate
 * @returns True if the data is a valid array of prompts
 */
export function validatePromptData(data: any): data is PromptData[] {
  if (!Array.isArray(data)) {
    logger.warning('Invalid prompt data: not an array', {
      module: 'storageUtils',
      function: 'validatePromptData',
      dataType: typeof data,
    });
    return false;
  }

  // Check if all items have the required properties
  const isValid = data.every(item => {
    const hasRequiredProps =
      typeof item === 'object' &&
      item !== null &&
      typeof item.id === 'string' &&
      typeof item.title === 'string' &&
      typeof item.text === 'string' &&
      Array.isArray(item.tags);

    if (!hasRequiredProps) {
      logger.warning('Invalid prompt item: missing required properties', {
        module: 'storageUtils',
        function: 'validatePromptData',
        item,
      });
    }

    return hasRequiredProps;
  });

  return isValid;
}

/**
 * Validates that the data is an array of folder objects
 * @param data The data to validate
 * @returns True if the data is a valid array of folders
 */
export function validateFolderData(data: any): data is FolderData[] {
  if (!Array.isArray(data)) {
    logger.warning('Invalid folder data: not an array', {
      module: 'storageUtils',
      function: 'validateFolderData',
      dataType: typeof data,
    });
    return false;
  }

  // Check if all items have the required properties
  const isValid = data.every((item, index) => {
    const hasRequiredProps =
      typeof item === 'object' &&
      item !== null &&
      typeof item.id === 'string' &&
      typeof item.name === 'string' &&
      typeof item.color === 'string' &&
      // isSpecial is optional on FolderData — legacy/repository-shaped records
      // omit it entirely; only reject when present with a non-boolean value.
      (item.isSpecial === undefined || typeof item.isSpecial === 'boolean');

    if (!hasRequiredProps) {
      logger.warning('Invalid folder item: missing required properties', {
        module: 'storageUtils',
        function: 'validateFolderData',
        itemIndex: index,
      });
    }

    return hasRequiredProps;
  });

  return isValid;
}

/**
 * Validates that the data is a valid user settings object
 * @param data The data to validate
 * @returns True if the data is a valid user settings object
 */
export function validateUserSettingsData(data: any): data is UserSettingsData {
  if (typeof data !== 'object' || data === null) {
    logger.warning('Invalid user settings data: not an object', {
      module: 'storageUtils',
      function: 'validateUserSettingsData',
      dataType: typeof data,
    });
    return false;
  }

  // Check if the object has the required properties
  const hasRequiredProps =
    (data.theme === 'light' || data.theme === 'dark') &&
    (data.premium_status === 'free' || data.premium_status === 'paid') &&
    typeof data.features === 'object' &&
    data.features !== null;

  if (!hasRequiredProps) {
    logger.warning('Invalid user settings: missing required properties', {
      module: 'storageUtils',
      function: 'validateUserSettingsData',
      data,
    });
  }

  // Validate nested arrays if they exist
  if (Array.isArray(data.folders) && !validateFolderData(data.folders)) {
    return false;
  }

  if (Array.isArray(data.prompts) && !validatePromptData(data.prompts)) {
    return false;
  }

  return hasRequiredProps;
}

/**
 * Safely extracts a value from a storage change
 * @param change The storage change object
 * @returns The extracted value or null
 */
export function extractValueFromStorageChange<T>(change: PlatformStorageChange): T | null {
  if (!change) return null;

  // Handle both wrapped and unwrapped values
  if (change.newValue?.data !== undefined) {
    return change.newValue.data as T;
  }

  return change.newValue as T;
}
